import { Button } from '@/components/ui/button';
import { Download } from 'lucide-react';
import { Severity } from '@kirin/api-client/contracts/shared/severity';
import {
  formatSubPolicyDescription,
  formatSubPolicyLabel,
  formatSubPolicySections,
} from '@kirin/api-client/helpers/alerts';
import { HighlightBox } from '@/components/shared/HighlightBox';
import AlertCard from './AlertCard';

export default function MaliciousExtensionDemo() {
  // Use public asset path - served directly by Vite
  const vsixUrl = '/demos/kirin-attack-demo-1.0.0.vsix';
  const hasVsixFile = true; // File is in public/demos/
  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-semibold mb-2">Steps to reproduce:</h3>
        <ol className="list-decimal ml-5 space-y-2 text-sm">
          <li>Download the demo extension using the button below</li>
          <li>In Cursor, open Command Palette (⌘/Ctrl+Shift+P)</li>
          <li>
            Run{' '}
            <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">
              Extensions: Install from VSIX...
            </code>
          </li>
          <li>Select the downloaded VSIX file</li>
          <li>Install the extension</li>
          <li>Check Kirin for the security alert</li>
        </ol>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold">Download demo extension:</h3>
        </div>
        {hasVsixFile ? (
          <Button asChild variant="default" className="gap-2">
            <a href={vsixUrl} download="kirin-attack-demo-1.0.0.vsix">
              <Download className="h-4 w-4" />
              Download VSIX
            </a>
          </Button>
        ) : (
          <HighlightBox variant="info" className="space-y-3">
            <p className="text-sm">
              <strong>Coming soon:</strong> The demo extension VSIX file is
              being prepared and will be available for download shortly.
            </p>
          </HighlightBox>
        )}
        <HighlightBox variant="warning" className="text-sm">
          <strong>⚠️ Note:</strong> This is a safe demonstration extension
          created specifically for testing. It includes terminal and task
          execution capabilities that trigger Kirin's behavior-based detection,
          but contains no actual malicious code.
        </HighlightBox>
      </div>

      <div>
        <h3 className="font-semibold mb-2">What triggers Kirin's detection:</h3>
        <ul className="list-disc ml-5 space-y-1 text-sm text-muted-foreground">
          <li>
            <strong>Publisher typosquatting</strong> - Publisher name "
            <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">
              curs0r
            </code>
            " closely resembles the trusted publisher "
            <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">
              cursor
            </code>
            " (edit distance: 1)
          </li>
          <li>
            <strong>Character substitution</strong> - Uses '0' (zero) instead of
            'o' to create a visually similar publisher name
          </li>
          <li>
            <strong>Terminal access capabilities</strong> - Extension can create
            and control terminal sessions
          </li>
          <li>
            <strong>Task execution capabilities</strong> - Extension can define
            and execute build tasks
          </li>
          <li>
            These combined signals indicate a sophisticated attack attempting to
            impersonate a trusted publisher while requesting dangerous
            permissions
          </li>
        </ul>
      </div>

      <div>
        <h3 className="font-semibold mb-2">Expected alerts in Kirin:</h3>
        <div className="space-y-3">
          <AlertCard
            title={formatSubPolicyLabel(
              'dangerousPermissions',
              'excessive_permissions'
            )}
            severity={Severity.High}
            description={formatSubPolicyDescription(
              'dangerousPermissions',
              'excessive_permissions',
              'Extension has terminal/task execution capabilities; Extension has auto-activation events: low-risk events (onStartupFinished)'
            )}
            descriptionSections={formatSubPolicySections(
              'dangerousPermissions',
              'excessive_permissions'
            )}
            details="Extension: curs0r.python (Python)"
          />
          <AlertCard
            title={formatSubPolicyLabel(
              'extensionsMaliciousDetection',
              'typosquatting'
            )}
            severity={Severity.Medium}
            description={formatSubPolicyDescription(
              'extensionsMaliciousDetection',
              'typosquatting',
              'Potential typo-squatting of trusted publisher "cursor" (edit distance: 1, malicious character substitutions detected)'
            )}
            descriptionSections={formatSubPolicySections(
              'extensionsMaliciousDetection',
              'typosquatting'
            )}
            details="Extension: curs0r.python (Python)"
          />
        </div>
      </div>
    </div>
  );
}
